// Standalone function to write a ManuscriptBackupPayload into DataRepository.
// Used by useRestoreBackup, useCloudSync, and any restore flow.

import type { ManuscriptBackupPayload } from '@/lib/backupPayload';
import type { DataRepository } from '@/types/repository';
import { createDefaultStructure, findFirstSceneId } from '@/hooks/usePlanner';
import { isWebCompanionPlatform } from '@/utils/platform';
import { androidRepository } from '@/lib/repository/androidRepository';
import { webRepository } from '@/lib/repository/webRepository';

/**
 * Writes a ManuscriptBackupPayload into the DataRepository (or IndexedDB/Web Cache).
 * Handles legacy V1 migration (creates default planner when missing).
 * Fixes lastModified Date serialization (coerces string → Date after JSON parse).
 *
 * Scene content and codex entries are fully replaced (old items are purged first)
 * so that deletions made on the source device propagate correctly.
 */
export async function processPayload(
  payload: ManuscriptBackupPayload,
  repo?: DataRepository
): Promise<void> {
  const targetRepo = repo || (isWebCompanionPlatform() ? webRepository : androidRepository);

  const rawPayload = payload as unknown as Record<string, unknown>;
  const plannerStruct = payload.plannerStructure || (rawPayload.planner as ManuscriptBackupPayload['plannerStructure']);
  const formatSettings = payload.formatSettings || (rawPayload.format as ManuscriptBackupPayload['formatSettings']);
  const sceneContent = payload.sceneContent || (rawPayload.scenes as ManuscriptBackupPayload['sceneContent']);
  const codexEntries = payload.codexEntries || (rawPayload.codex as ManuscriptBackupPayload['codexEntries']);

  // Determine if this backup has a truly valid planner structure
  const hasValidStructure = !!(plannerStruct && Array.isArray(plannerStruct.structure) && plannerStruct.structure.length > 0);

  if (payload.manuscript) {
    // Fix lastModified Date serialization: after JSON.parse, lastModified is a string, not a Date.
    // Coerce to Date to match the Manuscript type definition.
    if (payload.manuscript.lastModified && !(payload.manuscript.lastModified instanceof Date)) {
      payload.manuscript.lastModified = new Date(payload.manuscript.lastModified);
    }

    await targetRepo.putManuscript(payload.manuscript);
    
    // MIGRATION: If this backup lacks a valid planner, it's either legacy V1 or a corrupted transition state.
    // Construct a default planner and move the manuscript's prose into the first scene.
    if (!hasValidStructure) {
      const items = createDefaultStructure();
      await targetRepo.putPlannerStructure({ id: payload.manuscript.id, structure: items });
      const firstSceneId = findFirstSceneId(items);
      if (firstSceneId) {
        await targetRepo.putSceneContent({ 
          id: firstSceneId, 
          manuscriptId: payload.manuscript.id, 
          content: payload.manuscript.content || "" 
        });
      }
    }
  }

  if (hasValidStructure) await targetRepo.putPlannerStructure(plannerStruct!);
  if (payload.goals) await targetRepo.putGoals(payload.goals);
  if (formatSettings) await targetRepo.putFormatSettings(formatSettings);
  if (payload.series) await targetRepo.putSeries(payload.series);
  if (payload.collection) await targetRepo.putCollection(payload.collection);
  
  // Full-replace merge for scene content.
  //
  // ── Crash-safe ordering: WRITE NEW FIRST, DELETE OLD SECOND ──
  // Deletions on the source device must propagate, but we must never leave a
  // blank manuscript if the process is interrupted mid-way (tab crash, browser
  // kill, iOS memory pressure). By writing all incoming records before deleting
  // the old ones, a crash between the two phases leaves the original content
  // intact. A crash after delete but before write (the old order) would leave
  // an irrecoverable blank document.
  //
  // The brief window where both old and new records coexist is harmless because
  // all reads are by manuscriptId + sceneId — there is no ambiguity.
  if (payload.manuscript?.id) {
    if (sceneContent?.length) {
      // 1. Write all incoming scenes
      await Promise.all(sceneContent.map((scene) => targetRepo.putSceneContent(scene)));
      // 2. Only now delete records that the incoming set does not contain
      const incomingSceneIds = new Set(sceneContent.map((s) => s.id));
      const existingScenes = await targetRepo.getAllSceneContentForManuscript(payload.manuscript.id);
      const toDelete = existingScenes.filter(s => !incomingSceneIds.has(s.id));
      await Promise.all(toDelete.map(s => targetRepo.deleteSceneContent(s.id)));
    }

    if (codexEntries?.length) {
      // 1. Write all incoming codex entries
      await Promise.all(codexEntries.map((entry) => targetRepo.putCodexEntry(entry)));
      // 2. Only now delete records that the incoming set does not contain
      const incomingCodexIds = new Set(codexEntries.map((e) => e.id));
      const existingCodex = await targetRepo.getCodexEntriesForManuscript(payload.manuscript.id);
      const toDelete = existingCodex.filter(e => !incomingCodexIds.has(e.id));
      await Promise.all(toDelete.map(e => targetRepo.deleteCodexEntry(e.id)));
    }
  } else {
    // Fallback for payloads without manuscript (shouldn't happen, but be safe)
    if (sceneContent?.length) {
      await Promise.all(sceneContent.map((scene) => targetRepo.putSceneContent(scene)));
    }
    if (codexEntries?.length) {
      await Promise.all(codexEntries.map((entry) => targetRepo.putCodexEntry(entry)));
    }
  }

  // Persist the fully-processed payload to the web cache
  if (isWebCompanionPlatform() && (!repo || repo === webRepository)) {
    await webRepository.importBackupPayload(payload);
  }
}
